import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MobileScreenProps {
  titulo: string;
  /** Linha de apoio sob o título. Some quando não há o que dizer. */
  apoio?: string;
  children: ReactNode;
  /** A tela cuida da própria rolagem e do próprio respiro lateral. */
  semPadding?: boolean;
  /**
   * Sub-tela: mostra o alvo de voltar à esquerda do título.
   *
   * Vive na moldura e não em cada tela porque é exatamente o tipo de coisa que
   * a moldura existe para impedir de divergir: sem isto, a primeira sub-tela
   * inventaria o próprio cabeçalho e a segunda inventaria outro.
   */
  aoVoltar?: () => void;
  /** Rótulo acessível do voltar. Obrigatório quando `aoVoltar` existe. */
  rotuloVoltar?: string;
}

/**
 * A moldura das quatro telas da barra inferior.
 *
 * Existe porque as quatro nasceram cada uma com a sua: Projetos e Ajustes com
 * titulo de 20px e px-4 py-5, Tarefas sem titulo nenhum (comecava direto no
 * rotulo do primeiro grupo) e Notificacoes com titulo de 14px colado na borda,
 * herdado do popover que ela era antes. Alternar entre abas mostrava o conteudo
 * pulando de lugar, e telas irmas do mesmo nivel de navegacao pareciam de
 * produtos diferentes.
 *
 * Uma moldura so resolve isso hoje e impede a divergencia amanha: a quinta tela
 * que alguem criar nasce alinhada sem precisar consultar as outras quatro.
 */
const MobileScreen = ({
  titulo, apoio, children, semPadding = false, aoVoltar, rotuloVoltar,
}: MobileScreenProps) => (
  <div className="flex-1 overflow-y-auto">
    <div className="px-4 pt-5 pb-3">
      <div className="flex items-center gap-1">
        {aoVoltar && (
          <button
            type="button"
            data-testid="mobile-voltar"
            onClick={aoVoltar}
            aria-label={rotuloVoltar}
            className="-ml-3 w-11 h-11 shrink-0 grid place-items-center rounded-[var(--radius-md)] text-[var(--color-text-muted)]"
          >
            <ChevronLeft size={22} />
          </button>
        )}
        <h1 className="text-[20px] font-semibold text-[var(--color-text)] leading-tight min-w-0 truncate">
          {titulo}
        </h1>
      </div>
      {apoio && (
        <p className={cn('text-[13px] text-[var(--color-text-secondary)] mt-0.5', aoVoltar && 'pl-8')}>
          {apoio}
        </p>
      )}
    </div>
    <div className={cn(!semPadding && 'px-4 pb-5')}>{children}</div>
  </div>
);

export default MobileScreen;
